import React, { useState, useEffect } from 'react';
import { GridApiRef } from '@mui/x-data-grid-pro';
import { CancelToken } from 'axios';
import { useSnackbar } from 'notistack';
import { toStartOfDay } from '../../../../utilities/date-helper';
import { AssignCompanyTagsDialogComponent } from './assign-company-tags-dialog-component';
import { CircularProgressCentered } from '../../../circular-progress-centered/circular-progress-centered';
import {
  UpsertTagsCommand,
  ICompanyTagsService,
  CompanyTagsService,
  TagTypeOptionModel,
  UpsertTagsResult,
} from '../../../../areas/company/company-details/components/tags/company-tags-service';
import { useApiOnRender, useApi, useErrorSnackbar } from '../../../../hooks';
import { DatePickerValue } from '../../../date-picker/date-picker';

export interface IAssignCompanyTagsDialogProps {
  companyIds: string[];
}
export interface IAssignCompanyTagsDialogComponentStateModel {
  tagTypeId?: string;
  startDate?: Date | string;
  endDate?: Date | string;
}

export function AssignCompanyTagsDialog({ companyIds }: IAssignCompanyTagsDialogProps) {
  const [state, setState] = useState<IAssignCompanyTagsDialogComponentStateModel>({ startDate: new Date() } as IAssignCompanyTagsDialogComponentStateModel);
  const [errors, setErrors] = useState<IAssignCompanyTagsDialogComponentStateModel>({} as IAssignCompanyTagsDialogComponentStateModel);
  const [tagTypeOptions, setTagTypeOptions] = useState<TagTypeOptionModel[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const companyTagsService: ICompanyTagsService = new CompanyTagsService();

  const getTagTypeOptionsApi = useApiOnRender(() => companyTagsService.getTagTypeOptions(), [] as TagTypeOptionModel[]);
  const upsertTagsApi = useApi((cancelToken: CancelToken, request: UpsertTagsCommand) => companyTagsService.upsertTags(request), {} as UpsertTagsResult);
  const [isOpen, setIsOpen] = useState<boolean>(true);

  useErrorSnackbar(getTagTypeOptionsApi.error, upsertTagsApi.error);

  useEffect(() => {
    if (getTagTypeOptionsApi.isReady) {
      setTagTypeOptions(getTagTypeOptionsApi.result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTagTypeOptionsApi.isReady]);

  const handleConfirm = async () => {
    if (!handleValidate()) {
      return;
    }

    // Use companyIds from selected rows of table if gridApiRef is defined (ie. from company table instead of worklist), else use the companyIds set from the bulk action worklist
    const request: UpsertTagsCommand = {
      companyIds,
      tag: {
        tagTypeId: state.tagTypeId as string,
        startDate: state.startDate,
        endDate: state.endDate,
      },
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const response = await upsertTagsApi.invoke(request);
    if (response.result.companyIds) {
      enqueueSnackbar('Tag blev tildelt', { variant: 'success' });
    }
    setIsOpen(false);
  };

  async function handleChangeTagTypeOption(value: TagTypeOptionModel) {
    setState({ ...state, tagTypeId: value?.id });
    setErrors({ ...errors, tagTypeId: '' });
  }

  function handleStartDateChange(date: DatePickerValue) {
    setState({ ...state, startDate: date });
    setErrors({ ...errors, startDate: '' });
  }

  function handleEndDateChange(date: DatePickerValue) {
    setState({ ...state, endDate: date });
    setErrors({ ...errors, endDate: '' });
  }

  function handleValidate() {
    let isValid = true;

    if (!state.tagTypeId) {
      setErrors({ ...errors, tagTypeId: 'Tag type skal udfyldes' });
      isValid = false;
    }

    if (!state.startDate) {
      setErrors({ ...errors, startDate: 'Startdato skal angives' });
      isValid = false;
    }

    const userStartDate = toStartOfDay(state.startDate);
    const userEndDate = toStartOfDay(state.endDate);

    if (userStartDate && userEndDate) {
      if (userStartDate > userEndDate) {
        setErrors({ ...errors, startDate: 'Startdato skal være før eller på slutdato' });
        isValid = false;
      }
    }

    return isValid;
  }
  function handleClose() {
    setIsOpen(false);
  }
  return getTagTypeOptionsApi.isReady ? (
    <AssignCompanyTagsDialogComponent
      state={state}
      errors={errors}
      tagTypeOptions={tagTypeOptions}
      onChangeTagTypeOption={handleChangeTagTypeOption}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  ) : (
    <CircularProgressCentered />
  );
}
